// 1. 10. 2026 (C4): what a tier upgrade does to an item (the rules and the cost are in items/upgrade.ts).
// Rarity, perk, affixes and their rolls stay. The headline stat (damage, defense, capacity) moves with the
// catalog: the item keeps its place among the items of its slot (and weapon class), so a Kitchen Knife at
// T2 hits like the T2 knives (11 × 19.5 / 11.5 → 19). Every affix keeps its roll in the new tier's range;
// the item level becomes at least the new tier's first level; +N and the milestone boosts stay.

import { ITEM_TEMPLATES, TIER_LEVELS, headlineStat, itemTemplate, itemTier } from '../content/items';
import { affixQuality, affixRange } from '../itemgen';
import { pristineValue } from './pristine';
import type { GearSlot, ItemAffix, ItemInstance, ItemStatKey, ItemStats, WeaponClass } from '../types';
import type { Tier } from './upgrade';

const WHOLE: ItemStatKey[] = ['damage', 'armor', 'capacity', 'hp', 'str', 'agi', 'int', 'vit', 'per'];
const round = (v: number, k: ItemStatKey) => (WHOLE.includes(k) ? Math.round(v) : Math.round(v * 100) / 100);

const means = new Map<string, number | null>();

/**
 * The catalog's mean headline of a slot (a weapon: its class) at a tier. 23. 10. 2026: kept once worked out (an item worn
 * a tier ahead goes through here on every Power, `wornAs`).
 */
function headlineMean(slot: GearSlot, weaponClass: WeaponClass | undefined, tier: Tier): number | null {
  const key = `${slot}|${slot === 'weapon' ? (weaponClass ?? '') : ''}|${tier}`;
  if (means.has(key)) return means.get(key)!;
  const head = headlineStat(slot);
  const v = head ? ITEM_TEMPLATES.filter((t) => t.slot === slot && t.tier === tier && (slot !== 'weapon' || t.weaponClass === weaponClass)).map((t) => t.base[head] ?? 0) : [];
  const mean = v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  means.set(key, mean);
  return mean;
}

/**
 * The base stats on the new tier: the headline scaled by the catalog's means, the rest as they are. 23. 10. 2026: also to a
 * lower tier (an item worn a tier ahead, `wornAs` in items/tierWear.ts).
 */
export function tierUpBase(item: Pick<ItemInstance, 'templateId' | 'base' | 'tier'>, to: Tier): ItemStats {
  const t = itemTemplate(item.templateId);
  const head = headlineStat(t.slot);
  const from = headlineMean(t.slot, t.weaponClass, itemTier(item));
  const next = headlineMean(t.slot, t.weaponClass, to);
  if (!head || !from || !next || item.base[head] == null) return { ...item.base };
  return { ...item.base, [head]: Math.max(1, Math.round((item.base[head]! * next) / from)) };
}

/**
 * Each affix with the same roll in the new tier's range (affixes without a range stay as they are). A Pristine affix
 * stays Pristine: 1.3× the new tier's best (4. 10. 2026).
 */
export function tierUpAffixes(item: Pick<ItemInstance, 'templateId' | 'ilvl' | 'rarity' | 'tier' | 'affixes'>, to: Tier, ilvl: number): ItemAffix[] {
  return item.affixes.map((a) => {
    if (a.pristine) {
      const v = pristineValue({ ...item, ilvl, tier: to }, a.stat);
      return v == null ? { ...a } : { ...a, value: v };
    }
    const q = affixQuality(item, a);
    const r = affixRange(item.templateId, a.stat, ilvl, item.rarity, to);
    if (q == null || !r) return { ...a };
    const v = round(r[0] + (r[1] - r[0]) * q, a.stat);
    return { ...a, value: Math.max(v, WHOLE.includes(a.stat) ? 1 : 0.01) };
  });
}

/** The item on tier `to` (any tier above its own; the Relic Forge may skip one). */
export function applyTierUp<T extends ItemInstance>(item: T, to: Tier): T {
  if (to <= itemTier(item)) return item;
  const ilvl = Math.max(item.ilvl, TIER_LEVELS[to][0]);
  return { ...item, tier: to, ilvl, base: tierUpBase(item, to), affixes: tierUpAffixes(item, to, ilvl) };
}
